import { useRouter } from "next/router"
import React, { useCallback, useState } from "react"

import { Links } from "config/links"

import { gameLinks } from "components/layout/game-layout/game-links"
import { Box } from "components/shared/box"
import { BackButton } from "components/shared/game/back-button"
import BalanceBlock from "components/shared/game/balance-block"
import { Text } from "components/shared/text"

import { isPWA } from "utils/browser/is-pwa"

import { BurgerIcon } from "../icons/burger-icon"
import { Close } from "../icons/close"
import { CryptobotsText } from "../icons/cryptobots-text"
import { LogoDark } from "../icons/logo-dark_obsolete"
import { Button } from "../landing/button"
import { Link } from "../link"

export default function GameHeader({
  backBtnUrl,
  children,
  showBack = true,
  showBalance = true,
}: {
  backBtnUrl?: string
  children?: string | React.ReactNode
  showBack?: boolean
  showBalance?: boolean
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [backLoading, setBackLoading] = useState(false)
  const [enableLoadingOverlay, setEnableLoadingOverlay] = useState(false)
  const router = useRouter()
  const backLink = router.query.backLink as string
  const backTo = backBtnUrl || backLink

  const handleBack = useCallback(() => {
    setBackLoading(true)
    if (backTo) {
      router.push(backTo)
    } else {
      router.back()
    }
  }, [backTo, router])

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(!isMenuOpen)
  }, [isMenuOpen])

  const linkClick = useCallback(
    (route: Links) => () => {
      if (router.route !== route) {
        setEnableLoadingOverlay(true)
        router.push(route)
      }
      setIsMenuOpen(!isMenuOpen)
    },
    [isMenuOpen, router],
  )

  return (
    <>
      {enableLoadingOverlay && <Box className="fixed z-40 h-screen w-screen bg-black/40" />}
      <Box center className="z-100 w-full shrink-0 lg:mb-10 lg:pt-12">
        <Box row className="safe-pr w-full max-w-[1200px] items-center justify-between lg:pr-0">
          <Box center className="h-full items-center justify-center">
            {isPWA() ? (
              <Box row className={`lg:hidden ${!showBack ? "invisible" : ""}`}>
                <BackButton isNew isLoading={backLoading} onClick={handleBack} />
              </Box>
            ) : (
              <Box className="w-[10.4rem] items-start lg:hidden">
                <Box center className="h-[4.2rem] px-7">
                  <Button
                    noPadding
                    noOverlay
                    noVolume
                    type="transparent"
                    className="items-center justify-center"
                    onClick={toggleMenu}
                  >
                    {isMenuOpen ? <Close w={22} h={22} className="ml-1" /> : <BurgerIcon />}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
          <Text
            as="h1"
            className="text-shadow-without-blur-sm z-1 mt-[0.6rem] text-h3 uppercase leading-none lg:ml-[10.4rem]"
          >
            {children}
          </Text>
          <div className="w-[10.4rem]">{showBalance && <BalanceBlock />}</div>
        </Box>
        {isMenuOpen && (
          <>
            <Box
              row
              className="fixed top-[4.2rem] z-50 h-screen w-full justify-between border-t-2 border-white/5 bg-[#211056] p-12"
            >
              <Box className="gap-[32px] font-bold text-white">
                {gameLinks.map(({ link, label }, index) => (
                  <Link href={link} onClick={linkClick(link)} className="text-[1.8rem] leading-[2.5rem]" key={index}>
                    {label}
                  </Link>
                ))}
              </Box>
              <Box>
                <Link
                  href={Links.GAME}
                  onClick={linkClick(Links.GAME)}
                  className="relative -top-2 flex flex-row items-center justify-center"
                >
                  <LogoDark />
                  <CryptobotsText className="ml-2 w-[10rem]" />
                </Link>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </>
  )
}
